import {
  updateAdministratorBookingPolicy,
  updateCustomerBookingPolicy,
  updateEmployeeBookingPolicy,
  updateProfile,
  updateProfileLogo,
  createVenueSettings,
  updateVenueSettings,
  createRegionDateSettings,
  updateRegionDateSettings,
  addUser,
  updateUser,
  deleteUser,
  downgradeUser,
  assignRoles,
  createBay,
  updateBay,
  deleteBay,
} from "@/services/profileService";
import { useMutation, UseMutationResult } from "@tanstack/react-query";
import { AxiosError } from "axios";
import axiosInstance from "@/inteceptors";

export const useProfleUpdate = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  ProfileUpdatePayload
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    ProfileUpdatePayload
  >({
    mutationFn: updateProfile,
  });
};

export const useCustomerBookingPolicyUpdate = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  CustomerBookingPolicyPayload
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    CustomerBookingPolicyPayload
  >({
    mutationFn: updateCustomerBookingPolicy,
  });
};

export const useProfileUpdate = (): UseMutationResult<
  IResponse,
  AxiosError<IErrorResponse>,
  FormData
> => {
  return useMutation<IResponse, AxiosError<IErrorResponse>, FormData>({
    mutationFn: updateProfileLogo,
  });
};

export const useCreateRegionDateSettings = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  RegionDateSettingPayload
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    RegionDateSettingPayload
  >({
    mutationFn: createRegionDateSettings,
  });
};

export const useAdministratorBookingPolicyUpdate = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  AdminBookingPolicyPayload
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    AdminBookingPolicyPayload
  >({
    mutationFn: updateAdministratorBookingPolicy,
  });
};

export const useEmployeeBookingPolicyUpdate = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  EmployeeBookingPolicyPayload
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    EmployeeBookingPolicyPayload
  >({
    mutationFn: updateEmployeeBookingPolicy,
  });
};

export const useCreateVenueSettings = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  VenueSettingsPayload
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    VenueSettingsPayload
  >({
    mutationFn: createVenueSettings,
  });
};

export const useUpdateVenueSettings = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  VenueSettingsPayload & { id?: number }
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    VenueSettingsPayload & { id?: number }
  >({
    mutationFn: updateVenueSettings,
  });
};

export const useUpdateRegionDateSettings = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  RegionDateSettingPayload & { id?: number }
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    RegionDateSettingPayload & { id?: number }
  >({
    mutationFn: updateRegionDateSettings,
  });
};

export const useAddUser = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  AddUserPayload
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    AddUserPayload
  >({
    mutationFn: addUser,
  });
};

export const useUpdateUser = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  Partial<AddUserPayload> & { id?: number }
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    Partial<AddUserPayload> & { id?: number }
  >({
    mutationFn: updateUser,
  });
};

export const useDeleteUser = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  number
> => {
  return useMutation<ISuccessResponse, AxiosError<IErrorResponse>, number>({
    mutationFn: deleteUser,
  });
};

export const useDowngradeUser = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  number
> => {
  return useMutation<ISuccessResponse, AxiosError<IErrorResponse>, number>({
    mutationFn: downgradeUser,
  });
};

export const useAssignRoles = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  {
    userId: number;
    data: AssignRolesPayload;
  }
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    {
      userId: number;
      data: AssignRolesPayload;
    }
  >({
    mutationFn: assignRoles,
  });
};

interface InviteUserPayload {
  email: string;
  role_id: number;
  is_admin: boolean;
  is_coach?: boolean; // Optional, assigns Coach role in addition to primary role
}

export const useInviteUser = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  InviteUserPayload
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    InviteUserPayload
  >({
    mutationFn: (data) =>
      axiosInstance
        .post<ISuccessResponse>("/user/invite/", {
          ...data,
          is_coach: data.is_coach || false, // Ensure false if not provided
        })
        .then((res) => res.data),
  });
};

// Resend the admin onboarding invitation to an already-promoted admin (an
// existing User row). The generic /user/invite/ endpoint rejects existing
// emails, so this hits the dedicated resend endpoint instead.
export const useResendAdminInvite = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  { id: number }
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    { id: number }
  >({
    mutationFn: ({ id }) =>
      axiosInstance
        .post<ISuccessResponse>(`/user/admin-users/${id}/resend-invite/`)
        .then((res) => res.data),
  });
};

export const useCreateBay = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  Omit<Bay, "id" | "status" | "out_of_service" | "reason">
> => {
  return useMutation<
    ISuccessResponse,
    AxiosError<IErrorResponse>,
    Omit<Bay, "id" | "status" | "out_of_service" | "reason">
  >({
    mutationFn: createBay,
  });
};

export const useUpdateBay = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  Bay
> => {
  return useMutation<ISuccessResponse, AxiosError<IErrorResponse>, Bay>({
    mutationFn: updateBay,
  });
};

export const useDeleteBay = (): UseMutationResult<
  ISuccessResponse,
  AxiosError<IErrorResponse>,
  number
> => {
  return useMutation<ISuccessResponse, AxiosError<IErrorResponse>, number>({
    mutationFn: deleteBay,
  });
};
